// src/pages/stockTransfer/StockTransferListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  Plus,
  Eye,
  Package,
  Calendar,
  Warehouse as WarehouseIcon
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import { getStockTransfers, getAllWarehouses } from '../../services/api/stockTransfer';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate } from '../../utils/dateUtils';
import styles from './StockTransferListPage.module.css';

const StockTransferListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [transfers, setTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  const emptyFilters = {
    search: '',
    warehouseId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  };
  const [filters, setFilters] = useState(emptyFilters);

  const statusConfig = {
    draft: {
      label: t('stockTransfer.statusDraft'),
      color: 'var(--color-warning)',
      bgColor: 'var(--color-warning-bg)'
    },
    completed: {
      label: t('stockTransfer.statusCompleted'),
      color: 'var(--color-success)',
      bgColor: 'var(--color-success-bg)'
    }
  };

  useEffect(() => {
    if (!hasPermission('create_stock_transfer')) return;
    getAllWarehouses()
      .then(data => setWarehouses(data.warehouses || []))
      .catch(err => console.error('Failed to load warehouses:', err));
  }, []);

  useEffect(() => {
    fetchTransfers();
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchTransfers = async () => {
    try {
      setIsLoading(true);
      const params = { page: pagination.currentPage, limit: pagination.pageSize };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response = await getStockTransfers(params);
      setTransfers(response.transfers || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch transfers:', err);
      showError(err.response?.status === 403 ? t('errors.permissionDenied') : t('stockTransfer.failedToLoadTransfers'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (search) => {
    setFilters(prev => ({ ...prev, search }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('stockTransfer.warehouse'),
      type: 'select',
      icon: <WarehouseIcon size={18} />,
      placeholder: t('stockTransfer.allWarehouses'),
      options: warehouses.map(w => ({ value: w.id.toString(), label: w.name }))
    },
    {
      key: 'status',
      label: t('stockTransfer.status'),
      type: 'select',
      placeholder: t('stockTransfer.allStatuses'),
      options: Object.entries(statusConfig).map(([key, config]) => ({ value: key, label: config.label }))
    },
    {
      key: 'date',
      label: t('stockTransfer.dateRange'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  const columns = [
    {
      key: 'transferNumber',
      header: t('stockTransfer.transferNumber'),
      width: '200px',
      render: (row) => <div className={styles.adjustmentNumber}>{row.transferNumber}</div>
    },
    {
      key: 'fromWarehouse',
      header: t('stockTransfer.fromWarehouse'),
      width: '170px',
      render: (row) => (
        <div className={styles.warehouseCell}>
          <WarehouseIcon size={14} />
          {row.fromWarehouse?.name || t('common.na')}
        </div>
      )
    },
    {
      key: 'toWarehouse',
      header: t('stockTransfer.toWarehouse'),
      width: '170px',
      render: (row) => (
        <div className={styles.warehouseCell}>
          <WarehouseIcon size={14} />
          {row.toWarehouse?.name || t('common.na')}
        </div>
      )
    },
    {
      key: 'transferDate',
      header: t('stockTransfer.transferDate'),
      width: '140px',
      render: (row) => (
        <div className={styles.dateCell}>
          <Calendar size={14} />
          {formatDate(row.transferDate)}
        </div>
      )
    },
    {
      key: 'totalItems',
      header: t('stockTransfer.totalItems'),
      width: '110px',
      align: 'center',
      render: (row) => (
        <div className={styles.itemsCount}>
          <Package size={14} />
          {row.totalItems || 0}
        </div>
      )
    },
    {
      key: 'totalQuantity',
      header: t('stockTransfer.totalQuantity'),
      width: '120px',
      align: 'center',
      render: (row) => parseFloat(row.totalQuantity || 0)
    },
    {
      key: 'status',
      header: t('stockTransfer.status'),
      width: '130px',
      render: (row) => {
        const statusInfo = statusConfig[row.status] || statusConfig.draft;
        return (
          <div
            className={styles.statusBadge}
            style={{ backgroundColor: statusInfo.bgColor, borderColor: statusInfo.color, color: statusInfo.color }}
          >
            {statusInfo.label}
          </div>
        );
      }
    },
    {
      key: 'createdBy',
      header: t('stockTransfer.createdBy'),
      width: '150px',
      render: (row) => row.createdBy?.name || t('common.na')
    }
  ];

  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        <Link
          to={`/inventory/stock-transfers/${row.id}`}
          className={`${styles.actionButton} ${styles.viewButton}`}
          title={t('common.view')}
          onClick={(e) => e.stopPropagation()}
        >
          <Eye size={16} />
        </Link>
      </div>
    ),
    width: '80px',
    frozen: true
  };

  return (
    <div className={styles.stockTransferListPage}>
      <PageHeader
        title={t('stockTransfer.allStockTransfers')}
        description={t('stockTransfer.pageDescription')}
        icon={ArrowLeftRight}
        actions={
          hasPermission('create_stock_transfer') && (
            <Link to="/inventory/stock-transfers/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('stockTransfer.createStockTransfer')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('stockTransfer.searchPlaceholder')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (pageSize) => setPagination(prev => ({ ...prev, pageSize, currentPage: 1 })),
          options: pagination.pageSizeOptions
        }}
      />

      <DataTable
        data={transfers}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/inventory/stock-transfers/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={hasActiveFilters ? t('stockTransfer.noTransfersFound') : t('stockTransfer.noTransfersYet')}
        emptyIcon={<ArrowLeftRight size={64} />}
        emptyAction={
          hasActiveFilters ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_stock_transfer') ? (
            <Link to="/inventory/stock-transfers/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('stockTransfer.createStockTransfer')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default StockTransferListPage;
